import { onlyAlnum, validCnpj, validCpf } from '@divipay/br-documents';
import { DateTime } from 'luxon';
import { z } from 'zod';
import { addLateChargesIssues } from 'utils/lateCharges';

export const MIN_INSTALLMENTS = 2;
export const MAX_INSTALLMENTS = 24;
export const MIN_AMOUNT = 5;

/**
 * "Hoje" no fuso do servidor (SCHEDULED_CHARGE_TZ em carnet-plan.ts). Comparar
 * com a data UTC recusaria o próprio dia depois das 21h de Brasília.
 */
export const todayIsoDate = (): string => DateTime.now().setZone('America/Sao_Paulo').toISODate() as string;

/**
 * Espelha `apps/mobile/src/features/carnets/schemas.ts` e o
 * `CreateBilletCarnetDto` do servidor, que valida tudo de novo.
 */
export const createCarnetSchema = z
  .object({
    amountPerInstallment: z
      .number({
        required_error: 'Informe o valor da parcela',
        invalid_type_error: 'Informe o valor da parcela',
      })
      .min(MIN_AMOUNT, 'Parcela a partir de R$ 5,00'),
    // coerce: o <input type="number"> entrega texto. "" vira 0 e cai no mínimo.
    installments: z.coerce
      .number({ invalid_type_error: 'Informe a quantidade de parcelas' })
      .int('Informe um número inteiro de parcelas')
      .min(MIN_INSTALLMENTS, `Mínimo ${MIN_INSTALLMENTS} parcelas`)
      .max(MAX_INSTALLMENTS, `Máximo ${MAX_INSTALLMENTS} parcelas`),
    firstDueDate: z
      .string()
      .min(1, 'Vencimento da primeira parcela obrigatório')
      // yyyy-MM-dd compara certo como texto. Vazio já foi acusado acima.
      .refine((value) => !value || value >= todayIsoDate(), 'A primeira parcela não pode vencer no passado'),
    description: z.string().trim().min(1, 'Descrição obrigatória').max(80, 'Máx. 80 caracteres'),
    payerName: z.string().trim().min(1, 'Nome do pagador obrigatório').max(80, 'Máx. 80 caracteres'),
    payerDocument: z
      .string()
      .min(1, 'CPF/CNPJ do pagador obrigatório')
      .refine((value) => {
        if (!value) return true;
        // Alfanumérico, e não só dígitos, por causa do CNPJ com letras.
        const document = onlyAlnum(value);
        return validCpf(document) || validCnpj(document);
      }, 'Documento inválido'),
    payerEmail: z.string().email('E-mail inválido').or(z.literal('')),
    // Vazio herda o padrão da conta; zero isenta. Validados no superRefine,
    // porque o teto da multa fixa é o valor da parcela.
    latePenaltyMode: z.enum(['PERCENT', 'AMOUNT']).optional(),
    latePenaltyPercent: z.string(),
    latePenaltyAmount: z.number().optional(),
    lateMonthlyInterestPercent: z.string(),
    // Ausente = não. Sem .default(): ele separa o tipo de entrada do de saída e
    // o useForm deixa de casar com o resolver.
    withProtest: z.boolean().optional(),
  })
  .strict()
  .superRefine((values, ctx) => addLateChargesIssues(values, ctx, values.amountPerInstallment));

export type CreateCarnetValues = z.infer<typeof createCarnetSchema>;
